import { useState } from "react";
import { Copy, Check, Sparkles, Link2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Markdown } from "./Markdown";
import { ModelSources } from "./ModelSources";
import { AgreementMeter } from "./AgreementMeter";
import { DebatePanel } from "./DebatePanel";
import { SpeakButton } from "./SpeakButton";
import { KidsDebate } from "./KidsDebate";
import { MindBadge } from "./MindBadge";
import { ShareCardButton } from "./ShareCard";
import { MODELS } from "../lib/models";
import { shareTurn } from "../lib/api";

const ShareBtn = ({ chatId, index }) => {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const { share_id } = await shareTurn(chatId, index);
      const url = `${window.location.origin}/s/${share_id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied", { description: url });
      } catch {
        toast("Share link ready", { description: url, duration: 10000 });
      }
    } catch {
      toast.error("Couldn't create a share link");
    }
    setBusy(false);
  };
  return (
    <button data-testid="share-answer-btn" onClick={share} disabled={busy} className="text-slate-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-mono">
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} share
    </button>
  );
};

const CopyBtn = ({ text }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setDone(true);
    toast.success("Answer copied");
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button data-testid="copy-answer-btn" onClick={copy} className="text-slate-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs font-mono">
      {done ? <Check size={13} /> : <Copy size={13} />} {done ? "copied" : "copy"}
    </button>
  );
};

const PHASE_TEXT = {
  agreement: "Checking how much the AIs agree…",
  debate: "The AIs disagree, so they're debating each other…",
  merge: "Merging the best of every answer…",
  kids: "Checking the answer is safe for kids…",
  kids1: "The AIs are thinking about your idea…",
  kids2: "The AIs are answering each other…",
};

const Waiting = ({ phase, models }) => {
  const used = MODELS.filter((m) => models.includes(m.key));
  const names = used.map((m) => m.name);
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
  return (
    <div data-testid="multi-model-loading-indicator" className="flex items-center gap-3 py-2 text-sm text-slate-400">
      <span className="flex -space-x-1">
        {used.map((m, i) => (
          <span key={m.key} className="what-orb" style={{ background: m.color, animationDelay: `${i * 0.18}s` }} />
        ))}
      </span>
      <span data-testid="loading-phase-text">{PHASE_TEXT[phase] || `Asking ${list}${names.length > 1 ? " in parallel" : ""}…`}</span>
    </div>
  );
};

export const TurnView = ({ turn, index, chatId }) => {
  const live = turn.live;
  const phase = live ? turn.phase : null;
  const models = turn.models || MODELS.map((m) => m.key);
  const merger = MODELS.find((m) => m.key === turn.merger);
  const kids = turn.kids;
  const isDebate = turn.debating || turn.kids_debate?.length > 0;
  return (
    <section data-testid={`turn-${index}`} className="what-turn">
      <h2 data-testid="turn-question" className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight leading-snug">
        {turn.question}
      </h2>
      <KidsDebate turn={turn} index={index} chatId={chatId} />
      <MindBadge turn={turn} />
      {isDebate && !turn.live && chatId && turn.kids_debate?.length > 0 && <ShareCardButton chatId={chatId} index={index} />}
      <div data-testid="combined-answer-container" className={`what-answer mt-6 ${kids ? "is-kids" : ""}`}>
        <div className="flex flex-wrap items-center justify-between mb-4 gap-x-4 gap-y-2">
          {kids ? (
            <span data-testid="kids-safe-label" className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-[#38BDF8]">
              <ShieldCheck size={13} /> {turn.kids_blocked ? "Ask a grown-up" : turn.kids_debate?.length ? "The right answer" : "Kid-safe answer"}
            </span>
          ) : (
            <span data-testid="combined-answer-label" className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-[#D4FF3A]">
              <Sparkles size={13} /> {isDebate ? "Debate verdict" : models.length > 1 ? "Combined best answer" : "Answer"}
              {models.length > 1 && merger && <span className="text-slate-500 normal-case tracking-normal">· by {merger.name}</span>}
            </span>
          )}
          {turn.final && !live && (
            <span className="flex items-center gap-4">
              {chatId && <SpeakButton chatId={chatId} index={index} big={kids} />}
              {chatId && !kids && <ShareBtn chatId={chatId} index={index} />}
              <CopyBtn text={turn.final} />
            </span>
          )}
        </div>
        {turn.final ? (
          <Markdown testid="combined-answer-markdown" words={kids && !live ? turn.hard_words : undefined}>{turn.final}</Markdown>
        ) : turn.error ? (
          <p data-testid="answer-error" className="text-red-400 text-sm">{turn.error}</p>
        ) : (
          <Waiting phase={phase} models={models} />
        )}
        {kids && !live && turn.hard_words?.length > 0 && (
          <p data-testid="hard-words-hint" className="mt-4 text-sm text-[#7DD3FC]">💡 Tap the underlined words to find out what they mean!</p>
        )}
        {turn.agreement && !turn.kids_blocked && <AgreementMeter agreement={turn.agreement} after={turn.agreement_after} />}
      </div>
      {kids ? (
        !turn.kids_debate?.length && (turn.debated || (turn.debate && Object.keys(turn.debate).length > 0)) && (
          <p data-testid="kids-debate-note" className="mt-4 text-xs font-mono text-slate-500">The AIs talked it over to find the best answer.</p>
        )
      ) : isDebate ? null : (
        <>
          <DebatePanel turn={turn} />
          <p className="mt-6 mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-mono">Sources</p>
          <ModelSources answers={turn.answers} statuses={turn.statuses} models={models} />
        </>
      )}
    </section>
  );
};
